import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, CreditCard, KeyRound, Database, MessageSquare, MapPin, Cpu, Boxes, Radar, Wrench, HeartPulse, Handshake, Check, ArrowRight, ExternalLink, Calculator, Link2, CheckCheck, ShieldCheck, Globe, Mail, BarChart3, Lock, Zap, Save, Inbox } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MIGRATION_SERVICES } from "@/data/services";

const SVC_ICON = { shield: ShieldCheck, globe: Globe, mail: Mail, rocket: Rocket, link: Link2, chart: BarChart3, pin: MapPin, lock: Lock, zap: Zap, save: Save, inbox: Inbox };
import { useAuth } from "@/context/AuthContext";

const CAPABILITIES = [
  { icon: CreditCard, title: "Payments & Commerce", tools: ["Stripe", "PayPal", "Razorpay", "Paddle", "Lemon Squeezy", "Shopify"] },
  { icon: KeyRound, title: "Authentication", tools: ["Built-in Emergent Auth", "Google", "Firebase", "Auth0", "Clerk"] },
  { icon: Database, title: "Databases & Storage", tools: ["MongoDB (built-in)", "PostgreSQL", "Object storage", "Cloudinary", "AWS S3"] },
  { icon: MessageSquare, title: "Messaging & Comms", tools: ["Twilio SMS/WhatsApp", "SendGrid", "Resend", "Slack", "Telegram", "Discord"] },
  { icon: MapPin, title: "Google Services", tools: ["Maps", "Sheets", "Calendar", "Drive", "Analytics", "YouTube"] },
  { icon: Cpu, title: "AI & Machine Learning", tools: ["OpenAI", "Claude", "Gemini", "ElevenLabs", "Replicate", "Fal.ai"] },
  { icon: Boxes, title: "Everyday Tools", tools: ["Notion", "Airtable", "GitHub", "Mapbox", "Spotify"] },
];

export default function WhyEmergent() {
  const { user } = useAuth();
  const [referral, setReferral] = useState("https://app.emergent.sh");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => { if (r.data?.emergent_referral_url) setReferral(r.data.emergent_referral_url); }).catch(() => {});
  }, []);

  const sharePage = async () => {
    const url = `${window.location.origin}/why-emergent`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — share it with clients.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — link: " + url);
    }
  };

  const appLink = user ? "/pricing" : "/login";
  const scanLink = user ? "/dashboard" : "/login";
  const quoteLink = user ? "/pricing#intake" : "/login";

  const PATHS = [
    { icon: Radar, emoji: "🔍", title: "Scan", desc: "Free health check — score + a preview of what's wrong.", to: scanLink, cta: "Scan my site" },
    { icon: Wrench, emoji: "🛠️", title: "Fix", desc: "Do it yourself with our AI report ($29), or we fix it for you ($99).", to: appLink, cta: "Fix my site" },
    { icon: Rocket, emoji: "🚚", title: "Move", desc: "Migrate your site, DNS, email, domain & SSL to Emergent — tested & monitored.", to: quoteLink, cta: "Get a migration quote" },
    { icon: HeartPulse, emoji: "📡", title: "Manage", desc: "Ongoing monitoring, nightly re-scans and alerts — from $19.99/mo.", to: appLink, cta: "Start monitoring" },
    { icon: Handshake, emoji: "🤝", title: "Assign it to us", desc: "Hand the whole project over — we scope it, quote it, and handle it end-to-end.", to: quoteLink, cta: "Let us handle it", highlight: true },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="why-emergent-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-heading font-extrabold text-lg" data-testid="brand-logo">
            <img src="/brand-mark.png" alt="Link Website Pro" className="w-9 h-9 object-contain" />
            Link Website Pro
          </Link>
          <div className="flex items-center gap-3">
            <Link to={scanLink} className="text-sm text-zinc-300 hover:text-white transition-colors">{user ? "Dashboard" : "Sign in"}</Link>
            <a href={referral} target="_blank" rel="noreferrer" data-testid="nav-build-emergent" className="btn-brand text-white text-sm rounded-lg px-4 py-2 font-medium inline-flex items-center gap-1.5">
              Build on Emergent <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[32rem] h-[32rem] rounded-full bg-[#007AFF]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-24 w-[32rem] h-[32rem] rounded-full bg-[#9b5cff]/10 blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 py-20 sm:py-28 text-center relative">
          <div className="inline-flex items-center gap-2 text-[#4da3ff] mb-4">
            <Rocket size={16} /><span className="text-xs font-bold uppercase tracking-[0.25em]">Why Emergent</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
            Run your <span className="brand-gradient">entire business</span> from one platform
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
            Most owners pay for hosting, email, forms, payments, a CRM and a dozen tools that don't talk to each other. Emergent rebuilds your site with a real backend — then adds the everyday business tools you actually use, all managed and secured in one place.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href={referral} target="_blank" rel="noreferrer" data-testid="hero-build-emergent" className="btn-brand text-white rounded-full px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2">
              Build on Emergent <ExternalLink size={15} />
            </a>
            <Link to={quoteLink} data-testid="hero-quote" className="bg-transparent border border-white/20 hover:bg-white/10 text-white rounded-full px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 transition-colors">
              Get my free plan &amp; quote <ArrowRight size={15} />
            </Link>
          </div>
          <p className="text-zinc-500 text-xs mt-4">Not sure where you stand? <Link to={scanLink} className="text-[#4da3ff] hover:underline">Scan your site free first →</Link></p>
          <button data-testid="share-page-btn" onClick={sharePage} className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-white/15 hover:border-white/30 rounded-full px-4 py-2 transition-colors">
            {copied ? <><CheckCheck size={14} className="text-[#32D74B]" /> Copied!</> : <><Link2 size={14} /> Share this page</>}
          </button>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl font-bold tracking-tight">One platform replaces your whole stack</h2>
          <p className="text-zinc-400 mt-2">Everything below runs inside the site we build for you — no more juggling logins and invoices.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c) => (
            <div key={c.title} data-testid={`capability-${c.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="bg-[#121212] border border-white/10 rounded-2xl p-6 hover:border-white/25 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 border border-[#007AFF]/25 flex items-center justify-center mb-4">
                <c.icon size={18} className="text-[#4da3ff]" />
              </div>
              <h3 className="font-heading font-semibold text-white">{c.title}</h3>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {c.tools.map((t) => (
                  <span key={t} className="text-[11px] text-zinc-300 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiators */}
      <section className="max-w-5xl mx-auto px-5 pb-4">
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-xl px-4 py-3"><Check size={16} className="text-[#32D74B] shrink-0" /> We build &amp; move sites for you</div>
          <Link to={user ? "/migration" : "/login"} className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-xl px-4 py-3 hover:border-white/25 transition-colors"><Calculator size={16} className="text-[#4da3ff] shrink-0" /> Cut monthly costs — savings calculator</Link>
          <div className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-xl px-4 py-3"><Check size={16} className="text-[#32D74B] shrink-0" /> Best-value partner recommendations</div>
        </div>
      </section>

      {/* Paths */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl font-bold tracking-tight">How we get you there</h2>
          <p className="text-zinc-400 mt-2">Pick your path — do it yourself, or hand the whole thing to us.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PATHS.map((p) => (
            <div key={p.title} data-testid={`path-${p.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className={`relative bg-[#121212] rounded-2xl p-6 border flex flex-col ${p.highlight ? "border-[#007AFF]/50" : "border-white/10"}`}>
              {p.highlight && <span className="absolute -top-3 left-6 bg-[#007AFF] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Most hands-off</span>}
              <div className="flex items-center gap-2">
                <span className="text-xl">{p.emoji}</span>
                <h3 className="font-heading font-semibold text-white text-lg">{p.title}</h3>
              </div>
              <p className="text-zinc-400 text-sm mt-2 flex-1 leading-relaxed">{p.desc}</p>
              <Link to={p.to} className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${p.highlight ? "btn-brand text-white" : "bg-white/5 text-white border border-white/10 hover:bg-white/10"}`}>
                {p.cta} <ArrowRight size={15} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Everything we handle when moving off another host */}
      <section className="max-w-6xl mx-auto px-5 py-14" data-testid="migration-services">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl font-bold tracking-tight">Already on another host? We handle the whole move</h2>
          <p className="text-zinc-400 mt-2 max-w-2xl mx-auto">GoDaddy, Wix, Squarespace, WordPress — wherever you are, we take care of the technical chores so nothing breaks.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MIGRATION_SERVICES.map((s) => {
            const Icon = SVC_ICON[s.icon] || Check;
            return (
              <div key={s.title} data-testid={`service-${s.icon}`} className="flex gap-3 bg-[#121212] border border-white/10 rounded-xl p-5 hover:border-white/25 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[#32D74B]/10 border border-[#32D74B]/25 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-[#32D74B]" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white text-sm">{s.title}</h3>
                  <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <Link to={quoteLink} className="btn-brand text-white rounded-full px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2">
            Get a free move/upgrade quote <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-5 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-[#4da3ff]/30 p-10 sm:p-14 text-center" style={{ background: "linear-gradient(135deg, rgba(0,122,255,0.18), rgba(155,92,255,0.18))" }}>
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#4da3ff]/10 blur-3xl pointer-events-none" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight relative">Ready to run everything in one place?</h2>
          <p className="text-zinc-300 mt-3 max-w-xl mx-auto relative">Build it yourself on Emergent, or let us scan, move and manage it for you.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8 relative">
            <a href={referral} target="_blank" rel="noreferrer" data-testid="footer-build-emergent" className="btn-brand text-white rounded-full px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2">
              Build on Emergent <ExternalLink size={15} />
            </a>
            <Link to={quoteLink} className="bg-transparent border border-white/25 hover:bg-white/10 text-white rounded-full px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 transition-colors">
              Get a free quote <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-zinc-600 text-xs">
        <Link to="/" className="hover:text-zinc-400">← Back to Link Website Pro</Link>
      </footer>
    </div>
  );
}
